import { useRef, useState, type WheelEvent } from 'react';
import { getWieldedLoadout } from '@sorrem/shared';
import type { UIInventoryItem, UIItemContainerKind, UIPartyCharacter } from '@sorrem-client/types';
import { InlayPanel, ItemGrid, TextTabs, useItemTransferContext } from '@sorrem-client/ui/components';
import { toEquipmentSlots } from '@sorrem-client/ui/shared/equipment-slots';
import { LOADOUT_SLOTS } from '@sorrem-client/ui/shared/loadout-slots';
import { toUIInventoryItem } from '@sorrem-client/ui/shared/inventory-item';
import { useCharacterHomeCharacter } from '@sorrem-client/ui/features/CharacterHome/components/CharacterHomeItemTransfer';
import { countOccupiedSlots } from '@sorrem-client/ui/features/CharacterHome/tabs/InventoryTab/count-occupied-slots';
import styles from './CraftingItemStrip.module.scss';
import { toTourTargetProps } from '@sorrem-client/ui/shared/tour-targets';

type StripTab = 'equipped' | 'carried' | 'storage';

interface StripSlots {
    readonly items: Array<UIInventoryItem | undefined>;
    readonly totalSlots: number;
    readonly container: UIItemContainerKind;
    readonly label: string;
}

/** The slots a tab shows: the equipped ones in loadout order, else the carried or stored grid. */
function resolveStripSlots(character: UIPartyCharacter, tab: StripTab): StripSlots {
    if (tab === 'equipped') {
        // As the loadout shows them. Persisted loadouts fill empty slots with placeholder pieces with no item definition.
        const loadout = getWieldedLoadout(character);
        const items = (loadout ? toEquipmentSlots(loadout) : []).map((piece) => (piece?.itemDefinitionId ? toUIInventoryItem(piece) : undefined));
        return { items, totalSlots: items.length, container: 'equipment', label: 'Equipped items' };
    }
    if (tab === 'carried') {
        return { items: character.carriedItems, totalSlots: character.maxCarriedSlots, container: 'inventory', label: 'Carried items' };
    }
    return { items: character.storedItems, totalSlots: character.maxStoredSlots, container: 'storage', label: 'Stored items' };
}

/**
 * Crafting's item picker (#287): Equipped, Carried or Storage as one
 * scrolling row of its slots, the Inventory tab's own grid laid out in a
 * line. Clicking an item selects it, the selection every Character Home tab
 * shares. The loadout is hidden on this tab, so its equipped items are here,
 * each captioned with its place in the loadout (Primary, Off-Hand...).
 * Nothing moves here.
 */
export function CraftingItemStrip(): JSX.Element | null {
    const character = useCharacterHomeCharacter();
    const { selectedItemId, clearSelectedItem } = useItemTransferContext();
    const [tab, setTab] = useState<StripTab>('equipped');
    const rowRef = useRef<HTMLDivElement>(null);

    if (character === null) {
        return null;
    }
    const slots = resolveStripSlots(character, tab);
    const equipped = resolveStripSlots(character, 'equipped');

    // A plain mouse wheel scrolls the row sideways.
    const handleWheel = (event: WheelEvent<HTMLDivElement>): void => {
        if (rowRef.current !== null && event.deltaX === 0) {
            rowRef.current.scrollLeft += event.deltaY;
        }
    };

    return (
        <InlayPanel className={styles.craftingItemStrip} {...toTourTargetProps('crafting-items')}>
            <div className={styles.header}>
                <TextTabs
                    tabs={[
                        { id: 'equipped', label: `Equipped (${countOccupiedSlots(equipped.items)} / ${equipped.totalSlots})` },
                        { id: 'carried', label: `Carried (${countOccupiedSlots(character.carriedItems)} / ${character.maxCarriedSlots})` },
                        { id: 'storage', label: `Storage cache (${countOccupiedSlots(character.storedItems)} / ${character.maxStoredSlots})` },
                    ]}
                    activeTab={tab}
                    onTabChange={setTab}
                    ariaLabel='Items to work on'
                />
            </div>
            <div ref={rowRef} className={styles.row} onWheel={handleWheel}>
                <ItemGrid
                    key={tab}
                    totalSlots={slots.totalSlots}
                    items={slots.items}
                    container={slots.container}
                    size='large'
                    selectedItemId={selectedItemId}
                    onDeselectSelectedItem={clearSelectedItem}
                    ariaLabel={slots.label}
                    className={styles.slots}
                    renderSlotLabel={tab === 'equipped' ? (index) => <span className={styles.slotLabel}>{LOADOUT_SLOTS[index]?.label}</span> : undefined}
                />
            </div>
        </InlayPanel>
    );
}
